import type { POSSession } from '@burmazon/shared';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { useIdleTimeout } from './hooks/useIdleTimeout';
import { usePosSocket } from './hooks/usePosSocket';
import { apiGet, logout } from './lib/api';
import { useSessionStore } from './store/session';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const TerminalPage = lazy(() => import('./pages/TerminalPage'));
const OpenRegisterModal = lazy(() => import('./components/session/OpenRegisterModal'));

function isAuthenticated(): boolean {
  return !!localStorage.getItem('pos-accessToken');
}

function RequireAuth({ children }: { children: React.ReactNode }): React.ReactElement {
  // Log out automatically after 30 minutes of idle time
  useIdleTimeout(logout);
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />;
}

// ─── Session gate ─────────────────────────────────────────────────────────────
// After authentication, ensure there is an active session before showing
// the terminal. Shows the OpenRegisterModal if no session is found.

function SessionGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const {
    session,
    isLoading,
    setSession,
    setLoading,
    setShowOpenModal,
    showOpenModal,
    openRegister,
  } = useSessionStore();

  useEffect(() => {
    setLoading(true);
    apiGet<POSSession | null>('/pos/sessions/current')
      .then((s) => {
        if (s) {
          setSession(s);
        } else {
          setShowOpenModal(true);
        }
      })
      .catch(() => setShowOpenModal(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#0d1117',
          color: '#8b949e',
          fontSize: '1rem',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ fontSize: 24 }}>🏪</div>
        Checking register status…
      </div>
    );
  }

  if (!session || showOpenModal) {
    return (
      <Suspense fallback={null}>
        <OpenRegisterModal onOpen={openRegister} />
      </Suspense>
    );
  }

  return <>{children}</>;
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App(): React.ReactElement {
  // Connects to /pos namespace and invalidates product cache on inventory changes.
  // The hook enables itself once the access token is available in memory.
  usePosSocket();

  return (
    <BrowserRouter>
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
              background: '#1a1a2e',
              color: '#fff',
              fontSize: '1.25rem',
            }}
          >
            Loading POS…
          </div>
        }
      >
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <SessionGate>
                  <TerminalPage />
                </SessionGate>
              </RequireAuth>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
